export enum USER_ROLE {
  SUPER_ADMIN = "SUPER_ADMIN",
  COORDINATOR = "COORDINATOR", // web login ( mobile number & possword)
  MERCHANT = "MERCHANT", // web login  ( otp based login)
  SERVICE_PARTNER = "SERVICE_PARTNER",
  CUSTOMER = "CUSTOMER",
}

/**
 * `type` query param accepted by /api/v1/admin/user-lists
 */
export enum USER_TYPE {
  COORDINATOR = 0,
  MERCHANT = 1,
  SERVICE_PARTNER = 2,
  CUSTOMER = 3,
}

export const USER_ROLE_LABEL: { [key: string]: string } = {
  [USER_ROLE.SUPER_ADMIN]: "Super Admin",
  [USER_ROLE.COORDINATOR]: "Coordinator",
  [USER_ROLE.MERCHANT]: "Franchise",
  [USER_ROLE.SERVICE_PARTNER]: "Service Partner",
  [USER_ROLE.CUSTOMER]: "Customer",
};

export const USER_TYPE_TO_ROLE: { [key in USER_TYPE]: USER_ROLE } = {
  [USER_TYPE.COORDINATOR]: USER_ROLE.COORDINATOR,
  [USER_TYPE.MERCHANT]: USER_ROLE.MERCHANT,
  [USER_TYPE.SERVICE_PARTNER]: USER_ROLE.SERVICE_PARTNER,
  [USER_TYPE.CUSTOMER]: USER_ROLE.CUSTOMER,
};

export const ROLE_TO_USER_TYPE: { [key: string]: USER_TYPE } = {
  [USER_ROLE.COORDINATOR]: USER_TYPE.COORDINATOR,
  [USER_ROLE.MERCHANT]: USER_TYPE.MERCHANT,
  [USER_ROLE.SERVICE_PARTNER]: USER_TYPE.SERVICE_PARTNER,
  [USER_ROLE.CUSTOMER]: USER_TYPE.CUSTOMER,
};

export const getUserRoleLabel = (role?: string): string =>
  (role && USER_ROLE_LABEL[role]) || USER_ROLE_LABEL[USER_ROLE.CUSTOMER];

/**
 * Resolves the display label for a `type` query param value. `0` is a valid
 * type (coordinator), so the value is normalised instead of truthy-checked.
 */
export const getUserTypeLabel = (type: USER_TYPE | number | string): string => {
  const parsed = typeof type === "string" ? parseInt(type, 10) : type;
  return getUserRoleLabel(USER_TYPE_TO_ROLE[parsed as USER_TYPE]);
};

export const getRoleFromUserType = (
  type: USER_TYPE | number | string
): USER_ROLE => {
  const parsed = typeof type === "string" ? parseInt(type, 10) : type;
  return USER_TYPE_TO_ROLE[parsed as USER_TYPE] || USER_ROLE.CUSTOMER;
};

enum UserType {
  ADMIN = 0,
  TELECALLER = 1,
  MERCHANT = 2,
  STAFF = 3,
  END_USER = 4,
}

const UserTypeString: { [key in UserType]: string } = {
  [UserType.ADMIN]: "Admin",
  [UserType.TELECALLER]: "Coordinator",
  [UserType.MERCHANT]: "Franchise",
  [UserType.STAFF]: "Service Partner",
  [UserType.END_USER]: "Customer",
};

export const getUserTypeMessage = (status: string): string => {
  const statusCode = parseInt(status, 10);

  // Check if the status code is valid and return the corresponding message
  if (statusCode in UserTypeString) {
    return UserTypeString[statusCode as UserType];
  } else {
    return "Customer"; // Fallback for invalid status
  }
};

export const NavItems = [
  "utilities-management",
  "service-management",
  "service-pricing-management",
  "companies",
  "vehicle",
  "testimonial",
  "coupon",
];

export enum PAYMENT_STATUS {
  PENDING = 0,
  SUCCESS = 1,
  FAILED = 2,
  REFUNDED = 3,
  CANCELLED = 4,
}

export enum PAYMENT_MODE {
  COD_ONLINE = 0,
  ONLINE = 1,
  COD_CASH = 2,
}

export const PAYMENT_MODE_LABEL: { [key in PAYMENT_MODE]: string } = {
  [PAYMENT_MODE.COD_ONLINE]: "COD Online",
  [PAYMENT_MODE.ONLINE]: "Online",
  [PAYMENT_MODE.COD_CASH]: "COD Cash",
};

export const PAYMENT_STATUS_LABEL: { [key in PAYMENT_STATUS]: string } = {
  [PAYMENT_STATUS.PENDING]: "Pending",
  [PAYMENT_STATUS.SUCCESS]: "Success",
  [PAYMENT_STATUS.FAILED]: "Failed",
  [PAYMENT_STATUS.REFUNDED]: "Refunded",
  [PAYMENT_STATUS.CANCELLED]: "Cancelled",
};

export const PAYMENT_MODE_OPTIONS = (
  Object.values(PAYMENT_MODE).filter(
    (value) => typeof value === "number",
  ) as PAYMENT_MODE[]
).map((mode) => ({
  value: `${mode}`,
  label: PAYMENT_MODE_LABEL[mode],
}));

const hasEnumValue = (value: unknown): boolean =>
  value !== undefined && value !== null && `${value}` !== "";

export const getPaymentModeLabel = (mode: unknown): string => {
  if (!hasEnumValue(mode)) return "N/A";
  const code = Number(mode);
  if (code in PAYMENT_MODE_LABEL) {
    return PAYMENT_MODE_LABEL[code as PAYMENT_MODE];
  }
  return "N/A";
};

export const getPaymentStatusLabel = (status: unknown): string => {
  if (!hasEnumValue(status)) return "N/A";
  const code = Number(status);
  if (code in PAYMENT_STATUS_LABEL) {
    return PAYMENT_STATUS_LABEL[code as PAYMENT_STATUS];
  }
  return "N/A";
};

export enum ADDRESS_TYPE {
  HOME = 0,
  WORK = 1,
  OTHER = 2,
}

export const ADDRESS_TYPE_LABEL: { [key in ADDRESS_TYPE]: string } = {
  [ADDRESS_TYPE.HOME]: "Home",
  [ADDRESS_TYPE.WORK]: "Work",
  [ADDRESS_TYPE.OTHER]: "Other",
};

export enum DISCOUNT_STATUS {
  ACTIVE = 1,
  INACTIVE = 2,
  EXPIRED = 3,
  UPCOMING = 4,
}

export const DISCOUNT_STATUS_LABEL: { [key in DISCOUNT_STATUS]: string } = {
  [DISCOUNT_STATUS.ACTIVE]: "Active",
  [DISCOUNT_STATUS.INACTIVE]: "Inactive",
  [DISCOUNT_STATUS.EXPIRED]: "Expired",
  [DISCOUNT_STATUS.UPCOMING]: "Upcoming",
};

type DiscountStatusChipColor = "success" | "default" | "warning" | "info";

export const DISCOUNT_STATUS_COLOR: {
  [key in DISCOUNT_STATUS]: DiscountStatusChipColor;
} = {
  [DISCOUNT_STATUS.ACTIVE]: "success",
  [DISCOUNT_STATUS.INACTIVE]: "default",
  [DISCOUNT_STATUS.EXPIRED]: "warning",
  [DISCOUNT_STATUS.UPCOMING]: "info",
};

export const getDiscountStatusLabel = (status: unknown): string => {
  if (!hasEnumValue(status)) return "N/A";
  const code = Number(status);
  if (code in DISCOUNT_STATUS_LABEL) {
    return DISCOUNT_STATUS_LABEL[code as DISCOUNT_STATUS];
  }
  return "N/A";
};

export const getDiscountStatusColor = (
  status: unknown
): DiscountStatusChipColor => {
  const code = Number(status);
  if (code in DISCOUNT_STATUS_COLOR) {
    return DISCOUNT_STATUS_COLOR[code as DISCOUNT_STATUS];
  }
  return "default";
};